import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Pagination } from '../components/ui/pagination';
import { formatDate } from '../lib/utils';

const PAGE_SIZE = 20;

export function UsagePage() {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('start_datetime');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [filterTitle, setFilterTitle] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [selectedDetail, setSelectedDetail] = useState<any>(null);

  function handleSort(field: string) {
    if (sort === field) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field);
      setDir('desc');
    }
    setPage(1);
  }

  function handleDateFilter(value: string) {
    setFilterDate(value);
    const d = new Date();
    switch (value) {
      case '1': { // this month
        const start = new Date(d.getFullYear(), d.getMonth(), 1);
        const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        setFilterStartDate(start.toISOString().split('T')[0] as string);
        setFilterEndDate(end.toISOString().split('T')[0] as string);
        break;
      }
      case '2': { // this year
        setFilterStartDate(`${d.getFullYear()}-01-01`);
        setFilterEndDate(`${d.getFullYear()}-12-31`);
        break;
      }
      case '3': { // last year
        setFilterStartDate(`${d.getFullYear() - 1}-01-01`);
        setFilterEndDate(`${d.getFullYear() - 1}-12-31`);
        break;
      }
      default:
        setFilterStartDate('');
        setFilterEndDate('');
        break;
    }
    setPage(1);
  }

  function clearFilters() {
    setFilterTitle('');
    setFilterStatus('');
    setFilterDate('');
    setFilterStartDate('');
    setFilterEndDate('');
    setPage(1);
  }

  const { data: usageData, isLoading } = useQuery({
    queryKey: ['usage-list', page, sort, dir, filterTitle, filterStatus, filterStartDate, filterEndDate],
    queryFn: () =>
      api
        .get('/api/usage/list', {
          params: {
            offset: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
            sort,
            dir,
            ftr_title: filterTitle || undefined,
            ftr_startdate: filterStartDate || undefined,
            ftr_enddate: filterEndDate || undefined,
            ftr_status: filterStatus || undefined,
          },
        })
        .then((r) => r.data),
  });

  const usages = usageData?.query?.data ?? [];
  const total = usageData?.query?.total ?? 0;
  const totalRecords = usageData?.queryRecords?.total ?? total;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  function sortIcon(field: string) {
    if (sort !== field) return '↕';
    return dir === 'asc' ? '↑' : '↓';
  }

  async function loadDetail(trxId: string) {
    try {
      const r = await api.get('/api/usage/get', { params: { trx_id: trxId } });
      setSelectedDetail(r.data);
    } catch (err) {
      console.error('Failed to load usage detail:', err);
    }
  }

  const csvUrl = `/api/usage/download_csv?sort=${sort}&dir=${dir}${filterTitle ? `&ftr_title=${filterTitle}` : ''}${filterStartDate ? `&ftr_startdate=${filterStartDate}` : ''}${filterEndDate ? `&ftr_enddate=${filterEndDate}` : ''}${filterStatus ? `&ftr_status=${filterStatus}` : ''}`;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-3">Usage Details</h1>

      <div className="flex items-center justify-between mb-3">
        <p className="font-semibold text-[#767676]">
          Click the 'Download CSV' button to export and download your data in CSV format.
        </p>
        <a href={csvUrl} className="btn btn-secondary rounded-[4px] px-4 py-2 text-sm font-medium bg-[#cc0404] text-white hover:bg-[#a30303]">
          Download CSV
        </a>
      </div>

      {/* Empty state */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading usage data...</div>
      ) : totalRecords === 0 ? (
        <Card className="rounded-[20px]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">No usage details available!</h2>
              <img src="/assets/icon-action-graph.svg" alt="" className="w-16 h-16" />
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Filters */}
          <div className="flex gap-3 mb-4 items-end">
            <div className="w-1/6">
              <select
                className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
                value={filterDate}
                onChange={(e) => handleDateFilter(e.target.value)}
              >
                <option value="">Filter by date</option>
                <option value="1">This month</option>
                <option value="2">This year</option>
                <option value="3">Last year</option>
              </select>
            </div>
            <div className="w-7/12">
              <input
                type="text"
                className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
                placeholder="Search services list"
                value={filterTitle}
                onChange={(e) => { setFilterTitle(e.target.value); setPage(1); }}
              />
            </div>
            <div className="w-1/6">
              <select
                className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
              >
                <option value="">Filter by status</option>
                <option value="Failed">Failed</option>
                <option value="Successful">Success</option>
              </select>
            </div>
            <div className="w-1/12 pt-2">
              <button className="text-[#cc0404] hover:underline text-sm" onClick={clearFilters}>
                Clear All
              </button>
            </div>
          </div>

          {total > 0 ? (
            <Card className="rounded-[20px] mb-5">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-3 text-left cursor-pointer hover:text-[#cc0404]" onClick={() => handleSort('transaction_id')}>
                          Transaction Id {sortIcon('transaction_id')}
                        </th>
                        <th className="px-4 py-3 text-left cursor-pointer hover:text-[#cc0404]" onClick={() => handleSort('service_title')}>
                          Service Title {sortIcon('service_title')}
                        </th>
                        <th className="px-4 py-3 text-left cursor-pointer hover:text-[#cc0404]" onClick={() => handleSort('start_datetime')}>
                          Start Datetime {sortIcon('start_datetime')}
                        </th>
                        <th className="px-4 py-3 text-left cursor-pointer hover:text-[#cc0404]" onClick={() => handleSort('end_datetime')}>
                          End Datetime {sortIcon('end_datetime')}
                        </th>
                        <th className="px-4 py-3 text-left cursor-pointer hover:text-[#cc0404]" onClick={() => handleSort('status')}>
                          Status {sortIcon('status')}
                        </th>
                        <th className="px-4 py-3 text-left">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usages.map((u: any) => (
                        <tr key={u.transaction_id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3">{u.transaction_id}</td>
                          <td className="px-4 py-3">{u.service_title}</td>
                          <td className="px-4 py-3 text-[#767676]">{formatDate(u.start_datetime)}</td>
                          <td className="px-4 py-3 text-[#767676]">{formatDate(u.end_datetime)}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                              u.status === 'Successful'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {u.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              className="text-[#767676] hover:text-[#cc0404]"
                              onClick={() => loadDetail(u.transaction_id)}
                              title="View Details"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M19 19H5V5h7V3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="px-4 pb-4">
                    <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <p className="text-gray-500 py-4">No results found. Try a different search criteria.</p>
          )}
        </>
      )}

      {/* Usage detail offcanvas panel */}
      {selectedDetail && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelectedDetail(null)} />
          <div className="relative w-3/4 max-w-xl bg-white shadow-xl h-full overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h5 className="text-lg font-semibold">Usage Details</h5>
              <button className="text-[#767676] hover:text-[#cc0404]" onClick={() => setSelectedDetail(null)}>
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="col-span-2">
                  <label className="text-sm font-medium text-[#767676]">Transaction Id</label>
                  <p className="font-normal mb-4">{selectedDetail.queryTransaction?.transaction_id ?? '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#767676]">Service Title</label>
                  <p className="font-normal mb-4">{selectedDetail.queryTransaction?.service_title ?? '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#767676]">Status</label>
                  <p className="font-normal mb-4">{selectedDetail.queryTransaction?.status ?? '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#767676]">Start Datetime</label>
                  <p className="font-normal mb-4">{formatDate(selectedDetail.queryTransaction?.start_datetime)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#767676]">End Datetime</label>
                  <p className="font-normal mb-4">{formatDate(selectedDetail.queryTransaction?.end_datetime)}</p>
                </div>
              </div>

              <h6 className="font-semibold mb-3">Transaction Details</h6>
              {(selectedDetail.queryDetails?.length ?? 0) > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="px-2 py-2 text-left">Id</th>
                      <th className="px-2 py-2 text-left">Description</th>
                      <th className="px-2 py-2 text-left">Status</th>
                      <th className="px-2 py-2 text-left">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDetail.queryDetails.map((d: any) => (
                      <tr key={d.transaction_details_id} className="border-b">
                        <td className="px-2 py-2">{d.transaction_details_id}</td>
                        <td className="px-2 py-2">{d.description}</td>
                        <td className="px-2 py-2">{d.status}</td>
                        <td className="px-2 py-2 text-[#767676]">{formatDate(d.created)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-500">No detail records.</p>
              )}

              <div className="mt-6">
                <Button variant="secondary" onClick={() => setSelectedDetail(null)}>Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}